import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { TreeItemLayout } from '@fluentui/react-components/unstable'
import {
    Badge,
    Button,
    FlatTreeItem,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    tokens,
    Tooltip,
    useRestoreFocusTarget,
} from '@fluentui/react-components'
import {
    bundleIcon,
    EyeFilled,
    EyeRegular,
    FolderFilled,
    FolderOpenFilled,
    FolderRegular,
    MoreHorizontal20Filled,
    MoreHorizontal20Regular,
    OpenFilled,
    OpenRegular,
    RenameFilled,
    RenameRegular,
} from '@fluentui/react-icons'
import { DocumentFlatTreeItemProps, FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import FormattedDateTime from '../../../../components/Date/FormattedDateTime'
import { useTranslation } from 'react-i18next'
import { getDocumentExtension } from '../../../../helpers/documentHelper'
import StealthLink, { stealthClick } from '../../../../components/StealthLink'
import { Destination } from '../../../../context/AxiosContext'
import useAxios from '../../../../hooks/useAxios'
import { DocumentOpenType } from '../../../../context/GlobalContext'
import { getFileTypeIconAsUrl } from '@fluentui/react-file-type-icons'

const MoreIcon = bundleIcon(MoreHorizontal20Filled, MoreHorizontal20Regular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)
const PreviewIcon = bundleIcon(EyeFilled, EyeRegular)
const RenameIcon = bundleIcon(RenameFilled, RenameRegular)

export const useStyles = makeStyles({
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingVerticalS,
    },
    iconSizeSmall: {
        height: '16px',
        width: '16px',
        fontSize: '16px',
    },
    folderyellow: { color: tokens.colorPaletteYellowForeground1 },
    foldergrey: { color: tokens.colorPaletteAnchorBackground2 },
    folderdarkred: { color: tokens.colorPaletteRedForeground1 },
    folderlightred: { color: tokens.colorPaletteDarkOrangeBorder1 },
    folderdarkorange: { color: tokens.colorPalettePeachBorderActive },
    folderlightorange: { color: tokens.colorStatusWarningForegroundInverted },
    folderdarkgreen: { color: tokens.colorPaletteGreenForeground3 },
    folderlightgreen: { color: tokens.colorPaletteLightGreenForeground3 },
    folderdarkteal: { color: tokens.colorPaletteTealBorderActive },
    folderlightteal: { color: tokens.colorPaletteLightTealBackground2 },
    folderdarkblue: { color: tokens.colorPaletteBlueBorderActive },
    folderlightblue: { color: tokens.colorPaletteBlueBackground2 },
    folderdarkpurple: { color: tokens.colorPalettePurpleBorderActive },
    folderlightpurple: { color: tokens.colorPalettePurpleBackground2 },
    folderdarkpink: { color: tokens.colorPalettePinkBorderActive },
    folderlightpink: { color: tokens.colorPalettePinkBackground2 },
})

type FileDocumentFlatTreeItemProps = DocumentFlatTreeItemProps & {
    onPreviewClick: (driveItem: FolderDriveItem) => void
    onRenameClick: (driveItem: FolderDriveItem) => void
}

export default function FileDocumentTreeItem(props: FileDocumentFlatTreeItemProps): ReactElement {
    const axios = useAxios()
    const focusTargetAttribute = useRestoreFocusTarget()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const itemExtension = useMemo(() => getDocumentExtension(props.item.name), [props.item])

    const isFolder = useMemo(() => props.itemType === 'branch', [props.item])
    const isFile = useMemo(() => props.itemType === 'leaf', [props.item])
    const isOpen = useMemo(() => {
        return [...props.flatTreeHook.getTreeProps().openItems].some(id => id === props.value)
    }, [props.item, props.flatTreeHook.getTreeProps().openItems])

    const folderColor = useMemo(() => {
        if (isFolder) {
            const color: string = props.item.folder?.['decorator']?.['iconColor']
            return styles[`folder${color?.toLowerCase()}`]
        }
        return undefined
    }, [])

    const itemIcon = useMemo(() => {
        if (isFolder) {
            if (isOpen) {
                return <FolderOpenFilled className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            } else if (folderColor) {
                return <FolderFilled className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            } else {
                return <FolderRegular className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            }
        } else {
            return (
                <img
                    alt={`${itemExtension}`}
                    className={styles.iconSizeSmall}
                    src={getFileTypeIconAsUrl({ extension: itemExtension, type: 2, imageFileType: 'svg' })}
                />
            )
        }
    }, [folderColor, isFolder, isOpen])

    return (
        <FlatTreeItem {...props} {...focusTargetAttribute}>
            <TreeItemLayout
                expandIcon={isFolder ? itemIcon : undefined}
                iconBefore={!isFolder && itemIcon}
                iconAfter={
                    isFolder && (
                        <Badge size={'small'} appearance={'tint'}>
                            {props.item.childCount}
                        </Badge>
                    )
                }
                aside={isFile && <FormattedDateTime date={props.item.lastModifiedDateTime} />}
                actions={
                    <>
                        {isFile && (
                            <div className={styles.actions}>
                                <Tooltip relationship={'label'} withArrow content={translate('file.pivot.document.mutate.preview.button')}>
                                    <Button
                                        aria-label="Preview"
                                        appearance="subtle"
                                        icon={<PreviewIcon />}
                                        onClick={() => props.onPreviewClick?.(props.item)}
                                    />
                                </Tooltip>
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <Button aria-label="More options" appearance="subtle" icon={<MoreIcon />} />
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem
                                                icon={<OpenIcon />}
                                                onClick={() => {
                                                    stealthClick(
                                                        {
                                                            id: props.item.id,
                                                            destination: Destination.Documents,
                                                            documentsOpen: DocumentOpenType.Open,
                                                        },
                                                        axios,
                                                    )
                                                }}
                                            >
                                                {translate('action.open')}
                                            </MenuItem>
                                            <MenuItem icon={<RenameIcon />} onClick={() => props.onRenameClick?.(props.item)}>
                                                {translate('file.pivot.document.mutate.rename.button')}
                                            </MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </div>
                        )}
                        {isFolder && (
                            <div className={styles.actions}>
                                <Tooltip relationship={'label'} withArrow content={translate('file.pivot.document.mutate.rename.button')}>
                                    <Button
                                        aria-label="Rename"
                                        appearance="subtle"
                                        icon={<RenameIcon />}
                                        onClick={() => props.onRenameClick?.(props.item)}
                                    />
                                </Tooltip>
                            </div>
                        )}
                    </>
                }
            >
                {isFolder && props.item.displayName}
                {isFile && (
                    <StealthLink id={props.item.id} destination={Destination.Documents}>
                        {props.item.displayName}
                    </StealthLink>
                )}
            </TreeItemLayout>
        </FlatTreeItem>
    )
}
